import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { PropsWithChildren, useState } from 'react'
import { BrandVisualAnalysisFont } from 'types'
import Styles from './BrandWizardVisualAnalysisModal.module.scss'
import { FontFamilyComboBox } from 'components/VisualStudioPlayground/FontFamily/FontFamilyComboBox'
import { FontWeight } from 'screens/BrandStandard/FontWeight'
import { useHover } from '@react-aria/interactions'
import { FONT_WEIGHTS } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/constants'
import { getFontWeightAsNumber } from './utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Italic } from 'almanac-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'

interface Props
  extends PropsWithChildren<{
    label: string
    font: BrandVisualAnalysisFont
    size: number
    onChange: (font: BrandVisualAnalysisFont) => void
  }> {}

export const Font = ({ label, font, size, children, onChange }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const { hoverProps, isHovered } = useHover({})
  const { isMobile } = useResponsiveDesign()
  const fontsQuery = useFonts()

  const weight = getFontWeightAsNumber(font.weight)

  return (
    <div className={cx(Styles.font, { [Styles.hovered]: isHovered || isEditing })} {...hoverProps}>
      <div className={Styles.fontSidebar}>
        <Text variant="placeholder" color="var(--dark-40)">
          {label}
        </Text>
      </div>
      <div className={Styles.fontPreview}>
        <span
          style={{
            fontSize: size,
            fontFamily: font.family,
            fontWeight: weight,
            fontStyle: font.style,
          }}
        >
          {children}
        </span>
        <GoogleFontLoader family={font.family} />
        <BlazeFontLoader name={font.family} />
      </div>
      <div className={cx(Styles.fontControls, { [Styles.visible]: isEditing || isHovered || isMobile })}>
        <FontFamilyComboBox
          aria-label="Font family"
          size="sm"
          contentsClassName={Styles.fontInput}
          selectedKey={font.family}
          onOpenChange={setIsEditing}
          onSelectionChange={(family, weights) => {
            onChange({
              ...font,
              family: family as string,
              weight: weights.includes(weight) ? font.weight : FONT_WEIGHTS[400],
            })
          }}
          placeholder="Select a font"
          popoverMaxHeight={300}
          fonts={fontsQuery.data?.fonts || []}
        />
        <FontWeight
          italic={font.style === 'Italic'}
          aria-label="Font weight"
          weight={weight}
          onOpenChange={setIsEditing}
          onChange={(weight) => {
            onChange({ ...font, weight: FONT_WEIGHTS[weight] })
          }}
          fontFamily={font.family}
          size="sm"
          contentsClassName={Styles.fontInput}
        />
        <IconButton
          title="Italic"
          icon={Italic}
          size="sm"
          active={font.style === 'Italic'}
          variant="tertiary"
          onPress={() => {
            const style = font.style === 'Italic' ? 'Normal' : 'Italic'
            onChange({ ...font, style })
          }}
        />
      </div>
    </div>
  )
}
